import React, { useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Menu, X, ChevronDown, Instagram, Youtube } from 'lucide-react';
import { Button } from './ui/button';

// Custom X (Twitter) icon
const XIcon = ({ className }) => (
  <svg viewBox="0 0 24 24" className={className} fill="currentColor">
    <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z" />
  </svg>
);

// Custom TikTok icon
const TikTokIcon = ({ className }) => (
  <svg viewBox="0 0 24 24" className={className} fill="currentColor">
    <path d="M19.59 6.69a4.83 4.83 0 0 1-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 0 1-5.2 1.74 2.89 2.89 0 0 1 2.31-4.64 2.93 2.93 0 0 1 .88.13V9.4a6.84 6.84 0 0 0-1-.05A6.33 6.33 0 0 0 5 20.1a6.34 6.34 0 0 0 10.86-4.43v-7a8.16 8.16 0 0 0 4.77 1.52v-3.4a4.85 4.85 0 0 1-1-.1z" />
  </svg>
);

const socialLinks = [
  { url: 'https://www.instagram.com/levanta.life/', label: 'Instagram', icon: Instagram },
  { url: 'https://www.tiktok.com/@levanta.life', label: 'TikTok', icon: TikTokIcon },
  { url: 'https://www.youtube.com/@Levanta_life', label: 'YouTube', icon: Youtube },
  { url: 'https://x.com/Levantaapp', label: 'X', icon: XIcon }
];

const Header = () => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [aboutDropdown, setAboutDropdown] = useState(false);
  const location = useLocation();

  const navLinks = [
    { path: '/', label: 'Home' },
    { path: '/download-app', label: 'Download App' },
    { path: '/how-it-works', label: 'How Levanta Works' },
    { path: '/levanta-story', label: 'Levanta\'s Story' },
    { path: '/mission', label: 'Our Mission' },
    { path: '/vision', label: 'Our Vision' },
    { path: '/founder', label: 'About the Founder' },
    // { path: '/team', label: 'Team' },  // temporarily disabled
    { path: '/media-press', label: 'Media & Press' },
    { path: '/contact', label: 'Contact' },
    { path: '/faq', label: 'FAQ' }
  ];

  const aboutLinks = [
    { path: '/levanta-story', label: 'Levanta\'s Story' },
    { path: '/mission', label: 'Our Mission' },
    { path: '/vision', label: 'Our Vision' },
    { path: '/founder', label: 'About the Founder' },
    // { path: '/team', label: 'Team' }  // temporarily disabled
  ];

  return (
    <header className="sticky top-0 z-50 bg-white/90 backdrop-blur-md border-b border-gray-200 shadow-sm">
      <div className="max-w-7xl mx-auto px-6 py-2">
        <div className="flex items-center justify-between">
          {/* Logo */}
          <Link to="/" className="flex items-center gap-2">
            <img 
              src="/favicon.png" 
              alt="Levanta Logo" 
              className="h-10 w-10 rounded-lg"
            />
            <img 
              src="https://customer-assets.emergentagent.com/job_fff9b75d-1f9a-47f7-9e37-9066bc0d7456/artifacts/qgfu5fs6_Untitled%20design%20%282%29.svg" 
              alt="LEVANTA" 
              className="h-4"
            />
          </Link>

          {/* Desktop Navigation */}
          <nav className="hidden lg:flex items-center gap-6">
            <Link
              to="/"
              className={`text-sm font-medium transition-colors hover:text-blue-500 ${
                location.pathname === '/' ? 'text-blue-500' : 'text-gray-600'
              }`}
            >
              Home
            </Link>
            <Link
              to="/download-app"
              className={`text-sm font-medium transition-colors hover:text-blue-500 ${
                location.pathname === '/download-app' ? 'text-blue-500' : 'text-gray-600'
              }`}
            >
              Download App
            </Link>
            <Link
              to="/how-it-works"
              className={`text-sm font-medium transition-colors hover:text-blue-500 ${
                location.pathname === '/how-it-works' ? 'text-blue-500' : 'text-gray-600'
              }`}
            >
              How It Works
            </Link>
            
            {/* About Dropdown */}
            <div 
              className="relative"
              onMouseEnter={() => setAboutDropdown(true)}
              onMouseLeave={() => setAboutDropdown(false)}
            >
              <button className="text-sm font-medium text-gray-600 hover:text-blue-500 flex items-center gap-1 transition-colors py-2">
                About
                <ChevronDown className={`w-4 h-4 transition-transform ${aboutDropdown ? 'rotate-180' : ''}`} />
              </button>
              {aboutDropdown && (
                <div className="absolute top-full left-0 pt-1">
                  <div className="w-56 bg-white rounded-xl shadow-lg border border-gray-200 py-2">
                    {aboutLinks.map((link) => (
                      <Link
                        key={link.path}
                        to={link.path}
                        className="block px-4 py-2.5 text-sm text-gray-700 hover:bg-blue-50 hover:text-blue-500 transition-colors"
                      >
                        {link.label}
                      </Link>
                    ))}
                  </div>
                </div>
              )}
            </div>

            <Link
              to="/media-press"
              className={`text-sm font-medium transition-colors hover:text-blue-500 ${
                location.pathname === '/media-press' ? 'text-blue-500' : 'text-gray-600'
              }`}
            >
              Media & Press
            </Link>
            <Link
              to="/faq"
              className={`text-sm font-medium transition-colors hover:text-blue-500 ${
                location.pathname === '/faq' ? 'text-blue-500' : 'text-gray-600'
              }`}
            >
              FAQ
            </Link>
            <Link
              to="/contact"
              className={`text-sm font-medium transition-colors hover:text-blue-500 ${
                location.pathname === '/contact' ? 'text-blue-500' : 'text-gray-600'
              }`}
            >
              Contact
            </Link>
          </nav>

          {/* Download App Button - Desktop */}
          <div className="hidden lg:flex items-center">
            <Link to="/download-app">
              <Button className="bg-blue-500 hover:bg-blue-600 text-white">
                Download App
              </Button>
            </Link>
          </div>

          {/* Mobile: Download Button + Menu Button */}
          <div className="lg:hidden flex items-center gap-2">
            <Link to="/download-app">
              <Button className="bg-blue-500 hover:bg-blue-600 text-white text-sm px-3 py-2">
                Download
              </Button>
            </Link>
            <button
              onClick={() => setIsMenuOpen(!isMenuOpen)}
              className="p-2 text-gray-600 hover:text-gray-900"
            >
              {isMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
            </button>
          </div>
        </div>

        {/* Mobile Menu */}
        {isMenuOpen && (
          <div className="lg:hidden mt-4 pb-4 border-t border-gray-200 pt-4">
            <nav className="flex flex-col gap-4">
              {navLinks.map((link) => (
                <Link
                  key={link.path}
                  to={link.path}
                  onClick={() => setIsMenuOpen(false)}
                  className={`text-sm font-medium transition-colors hover:text-blue-500 ${
                    location.pathname === link.path ? 'text-blue-500' : 'text-gray-600'
                  }`}
                >
                  {link.label}
                </Link>
              ))}
            </nav>
            {/* Social Media Links in Mobile Menu */}
            <div className="mt-6 pt-4 border-t border-gray-200">
              <p className="text-xs text-gray-500 mb-3">Follow us</p>
              <div className="flex gap-3">
                {socialLinks.map((social) => (
                  <a
                    key={social.label}
                    href={social.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="w-9 h-9 bg-gray-100 hover:bg-blue-100 rounded-full flex items-center justify-center transition-colors"
                    aria-label={social.label}
                  >
                    <social.icon className="w-4 h-4 text-gray-600" />
                  </a>
                ))}
              </div>
            </div>
          </div>
        )}
      </div>
    </header>
  );
};

export default Header;